import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { toast } from "react-toastify";

import AppPageTitle from "../../components/AppPageTitle";
import BulkInfoModal from "../../components/BulkInfoModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import PermissionModal from "../../components/PermissionModal";
import { AuthContext } from "../../context/Auth/AuthContext";
import toastError from "../../errors/toastError";
import apiV2 from "../../services/apiV2";
import { i18n } from "../../translate/i18n";

const BULK_STATUS_CLASS = {
	stopped: "bulk-status-badge--stopped",
	running: "bulk-status-badge--running",
	unstarted: "bulk-status-badge--unstarted",
	cancelled: "bulk-status-badge--cancelled",
	complete: "bulk-status-badge--complete",
};

const EDITABLE_BULK_STATUSES = ["unstarted", "stopped", "running"];

const canEditBulkMessage = (bulkMessage) =>
	EDITABLE_BULK_STATUSES.includes(bulkMessage?.status);

function IconPlus({ className = "size-4 me-1 shrink-0" }) {
	return (
		<svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<line x1="12" y1="5" x2="12" y2="19" />
			<line x1="5" y1="12" x2="19" y2="12" />
		</svg>
	);
}

function IconEdit() {
	return (
		<svg className="size-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
			<path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
		</svg>
	);
}

function IconPlay() {
	return (
		<svg className="size-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<polygon points="5 3 19 12 5 21 5 3" />
		</svg>
	);
}

function IconPause() {
	return (
		<svg className="size-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<rect x="6" y="4" width="4" height="16" />
			<rect x="14" y="4" width="4" height="16" />
		</svg>
	);
}

function IconRepeat() {
	return (
		<svg className="size-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<polyline points="17 1 21 5 17 9" />
			<path d="M3 11V9a4 4 0 0 1 4-4h14" />
			<polyline points="7 23 3 19 7 15" />
			<path d="M21 13v2a4 4 0 0 1-4 4H3" />
		</svg>
	);
}

function IconRotateLeft() {
	return (
		<svg className="size-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<polyline points="1 4 1 10 7 10" />
			<path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" />
		</svg>
	);
}

function IconReportDetails() {
	return (
		<svg className="size-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2" />
			<rect x="9" y="3" width="6" height="4" rx="1" />
			<line x1="9" y1="12" x2="15" y2="12" />
			<line x1="9" y1="16" x2="15" y2="16" />
			<line x1="9" y1="8" x2="13" y2="8" />
		</svg>
	);
}

function IconCopy() {
	return (
		<svg className="size-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
			<path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
		</svg>
	);
}

function IconDelete() {
	return (
		<svg className="size-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<polyline points="3 6 5 6 21 6" />
			<path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
		</svg>
	);
}

function formatTriggerDate(trigger) {
	return new Date(trigger)
		.toLocaleDateString("pt-BR", {
			timeZone: "America/Sao_Paulo",
			hour: "2-digit",
			minute: "2-digit",
		})
		.split(", ")
		.join(" - ");
}

function computeDelays(data) {
	data.forEach((bulkMessage) => {
		if (bulkMessage.status !== "running") {
			return;
		}

		const now = new Date();
		let trigger = new Date(bulkMessage.trigger);
		let diff = trigger.getTime() - now.getTime();

		if (diff < 0) {
			diff = 5000;
			trigger = new Date(now.getTime() + 5000);
		}

		const contacts = bulkMessage.contacts || [];
		const pending = contacts.filter((c) => c.jobStatus === null).length;
		const messagesIntervalTime = pending * bulkMessage.messageInterval;
		const clustersIntervalTime =
			(pending / bulkMessage.clusterSize - 1) * bulkMessage.clusterInterval;
		const delayTime = messagesIntervalTime + clustersIntervalTime;
		bulkMessage.delay = new Date(trigger.getTime() + delayTime * 1000);
	});

	return data;
}

function formatEstimatedEnd(bulkMessage) {
	if (bulkMessage.status !== "running") {
		return "—";
	}
	if (!bulkMessage.delay) {
		return "—";
	}
	const date = new Date(bulkMessage.delay);
	if (Number.isNaN(date.getTime())) {
		return "—";
	}
	return date.toLocaleString("pt-BR");
}

/** Nome do template (API oficial) para exibir na listagem. */
function getBulkTemplateLabel(bulkMessage) {
	if (!bulkMessage?.templateId && !bulkMessage?.customTemplateId) {
		return null;
	}
	const fromMessage = String(bulkMessage.message || "").trim();
	if (fromMessage) return fromMessage;
	return String(bulkMessage.templateId || bulkMessage.customTemplateId || "").trim() || null;
}

function BulkCampaignNameCell({ bulkMessage }) {
	const templateLabel = getBulkTemplateLabel(bulkMessage);
	return (
		<div className="min-w-0">
			<p className="m-0 truncate text-sm font-semibold text-default-800 dark:text-default-100">
				{bulkMessage.name}
			</p>
			{templateLabel && (
				<p
					className="mt-0.5 mb-0 truncate text-xs text-default-500 dark:text-default-400"
					title={templateLabel}
				>
					Template: {templateLabel}
				</p>
			)}
		</div>
	);
}

function BulkInfoItem({ label, value }) {
	return (
		<div>
			<p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-default-500 dark:text-default-400">
				{label}
			</p>
			<p className="m-0 text-sm text-default-700 dark:text-default-200">{value}</p>
		</div>
	);
}

function BulkMobileCard({
	bulkMessage,
	statusLabel,
	user,
	handleEditBulkMessages,
	handleBulkMessagingStatus,
	handleResendAllMessages,
	getFailedContacts,
	failedLoading,
	failedContacts,
	handleResendFailedMessages,
	handleOpenBulkInfosModal,
	duplicateBulkMessage,
	setDeletingBulkMessages,
	setConfirmModalOpen,
	setPermissionOpen,
}) {
	return (
		<div className="rounded-2xl border border-default-200 bg-white p-4 shadow-sm dark:border-default-700 dark:bg-default-950">
			<div className="mb-4 flex items-start justify-between gap-3">
				<div className="min-w-0">
					<BulkCampaignNameCell bulkMessage={bulkMessage} />
					<p className="mt-1 mb-0 text-xs text-default-500 dark:text-default-400">
						Protocolo #{bulkMessage.protocol}
					</p>
				</div>
				<span
					className={`bulk-status-badge ${BULK_STATUS_CLASS[bulkMessage.status] || ""}`}
				>
					{statusLabel(bulkMessage.status)}
				</span>
			</div>

			<div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
				<BulkInfoItem
					label={i18n.t("bulkMessaging.table.sendDate")}
					value={formatTriggerDate(bulkMessage.trigger)}
				/>
				<BulkInfoItem
					label={i18n.t("bulkMessaging.table.estimatedEnd")}
					value={formatEstimatedEnd(bulkMessage)}
				/>
			</div>

			<div className="mt-4 flex flex-wrap gap-2">
				{canEditBulkMessage(bulkMessage) && (
					<button
						type="button"
						className="bulk-action-btn"
						title={i18n.t("bulkMessaging.actions.edit")}
						onClick={() => handleEditBulkMessages(bulkMessage)}
					>
						<IconEdit />
					</button>
				)}

				{bulkMessage.status === "unstarted" && (
					<button
						type="button"
						className="bulk-action-btn"
						title={i18n.t("bulkMessaging.actions.start")}
						onClick={() => handleBulkMessagingStatus(bulkMessage, "running")}
					>
						<IconPlay />
					</button>
				)}

				{bulkMessage.status === "running" && (
					<button
						type="button"
						className="bulk-action-btn"
						title={i18n.t("bulkMessaging.actions.pause")}
						onClick={() => handleBulkMessagingStatus(bulkMessage, "stopped")}
					>
						<IconPause />
					</button>
				)}

				{bulkMessage.status === "stopped" && (
					<button
						type="button"
						className="bulk-action-btn"
						title={i18n.t("bulkMessaging.actions.resume")}
						onClick={() => handleBulkMessagingStatus(bulkMessage, "running")}
					>
						<IconPlay />
					</button>
				)}

				{(bulkMessage.status === "cancelled" || bulkMessage.status === "complete") && (
					<>
						<button
							type="button"
							className="bulk-action-btn"
							title={i18n.t("bulkMessaging.actions.resendAllHint")}
							onClick={() => handleResendAllMessages(bulkMessage)}
						>
							<IconRepeat />
						</button>

						{bulkMessage.contacts.find((contact) => contact.jobStatus === false) && (
							<button
								type="button"
								className="bulk-action-btn bulk-action-btn--warn"
								title={
									failedLoading
										? i18n.t("bulkMessaging.actions.loading")
										: i18n.t("bulkMessaging.actions.resendFailed", {
												count: failedContacts,
										  })
								}
								onMouseEnter={() => getFailedContacts(bulkMessage.id)}
								onClick={() => handleResendFailedMessages(bulkMessage)}
							>
								<IconRotateLeft />
							</button>
						)}
					</>
				)}

				<button
					type="button"
					className="bulk-action-btn"
					title={i18n.t("bulkMessaging.actions.details")}
					onClick={() => handleOpenBulkInfosModal(bulkMessage)}
				>
					<IconReportDetails />
				</button>

				<button
					type="button"
					className="bulk-action-btn"
					title={i18n.t("bulkMessaging.actions.duplicate")}
					onClick={() => duplicateBulkMessage(bulkMessage)}
				>
					<IconCopy />
				</button>

				<button
					type="button"
					className="bulk-action-btn"
					title={i18n.t("bulkMessaging.actions.delete")}
					onClick={() => {
						if (user.profile === "admin") {
							setDeletingBulkMessages(bulkMessage);
							setConfirmModalOpen(true);
						} else {
							setPermissionOpen(true);
						}
					}}
				>
					<IconDelete />
				</button>
			</div>
		</div>
	);
}

const BulkMessaging = () => {
	const history = useHistory();
	const location = useLocation();
	const [loading, setLoading] = useState(false);
	const [bulkMessages, setBulkMessages] = useState([]);
	const [selectedBulk, setSelectedBulk] = useState(null);
	const [bulkInfosModalOpen, setBulkInfosModalOpen] = useState(false);
	const [confirmModalOpen, setConfirmModalOpen] = useState(false);
	const [deletingBulkMessages, setDeletingBulkMessages] = useState(null);
	const [permissionOpen, setPermissionOpen] = useState(false);
	const [failedContacts, setFailedContacts] = useState(null);
	const [failedLoading, setFailedLoading] = useState(false);

	const { user } = useContext(AuthContext);
	const bulkMessagesRef = useRef(bulkMessages);
	bulkMessagesRef.current = bulkMessages;

	const refreshBulkMessages = useCallback(async () => {
		if (user?.companyId == null) return;
		const { data } = await apiV2.get("/bulk-messaging", {
			params: { companyId: user.companyId },
		});
		setBulkMessages(computeDelays(data));
	}, [user?.companyId]);

	useEffect(() => {
		if (user?.companyId == null) return undefined;
		setLoading(true);
		refreshBulkMessages()
			.catch(toastError)
			.finally(() => setLoading(false));
	}, [location.pathname, refreshBulkMessages, user?.companyId]);

	useEffect(() => {
		if (user?.companyId == null) return undefined;

		const interval = setInterval(() => {
			const hasRunning = bulkMessagesRef.current.some((b) => b.status === "running");
			if (!hasRunning) return;
			refreshBulkMessages().catch(toastError);
		}, 5000);

		return () => clearInterval(interval);
	}, [refreshBulkMessages, user?.companyId]);

	const getFailedContacts = async (bulkMessageId) => {
		setFailedContacts(null);
		setFailedLoading(true);
		try {
			const { data } = await apiV2.get(`/bulk-messaging/${bulkMessageId}/failed`, {
				params: { companyId: user.companyId },
			});
			setFailedContacts(data);
		} catch (err) {
			toastError(err);
		} finally {
			setFailedLoading(false);
		}
	};

	const handleEditBulkMessages = (bulkMessage) => {
		history.push(`/bulk-messaging/${bulkMessage.id}/editar`);
	};

	const handleOpenBulkInfosModal = (bulkMessage) => {
		setSelectedBulk(bulkMessage);
		setBulkInfosModalOpen(true);
	};

	const handleCloseBulkInfosModal = () => {
		setSelectedBulk(null);
		setBulkInfosModalOpen(false);
	};

	const handleBulkMessagingStatus = async (bulkMessage, status) => {
		try {
			await apiV2.put(
				`/bulk-messaging-handle/${bulkMessage.id}`,
				{ status },
				{ params: { companyId: user.companyId } }
			);
			await refreshBulkMessages();
		} catch (err) {
			toastError(err);
		}
	};

	const handleResendAllMessages = async (bulkMessage) => {
		try {
			await apiV2.put(
				`/bulk-messaging-handle/${bulkMessage.id}/resend-all`,
				{},
				{ params: { companyId: user.companyId } }
			);
			await refreshBulkMessages();
		} catch (err) {
			toastError(err);
		}
	};

	const duplicateBulkMessage = async (bulkMessage) => {
		try {
			const response = await apiV2.post(
				`/bulk-messaging/${bulkMessage.id}/duplicate`,
				{},
				{ params: { companyId: user.companyId } }
			);
			setBulkMessages((prev) => computeDelays([...prev, response.data]));
		} catch (err) {
			toastError(err);
		}
	};

	const handleResendFailedMessages = async (bulkMessage) => {
		try {
			await apiV2.put(
				`/bulk-messaging-handle/${bulkMessage.id}/resend-failed`,
				{},
				{ params: { companyId: user.companyId } }
			);
			await refreshBulkMessages();
		} catch (err) {
			toastError(err);
		}
	};

	const handleDeleteBulkMessages = async (bulkMessageId) => {
		try {
			await apiV2.delete(`/bulk-messaging/${bulkMessageId}`, {
				params: { companyId: user.companyId },
			});
			setBulkMessages((prev) => prev.filter((b) => b.id !== bulkMessageId));
			toast.success(i18n.t("bulkMessaging.toasts.deleted"));
		} catch (err) {
			toastError(err);
		}
	};

	const statusLabel = (status) =>
		i18n.t(`bulkMessaging.status.${status}`, { defaultValue: status });

	return (
		<div className="app-admin-page">
			<PermissionModal open={permissionOpen} onClose={setPermissionOpen} />

			<ConfirmationModal
				title={i18n.t("bulkMessaging.confirmDeleteTitle")}
				open={confirmModalOpen}
				onClose={setConfirmModalOpen}
				onConfirm={() => handleDeleteBulkMessages(deletingBulkMessages?.id)}
			>
				{i18n.t("messageOptionsMenu.confirmationModal.message")}
			</ConfirmationModal>

			<BulkInfoModal
				open={bulkInfosModalOpen}
				onClose={handleCloseBulkInfosModal}
				bulkMessageId={selectedBulk?.id}
			/>

			<div className="app-admin-page__scroll">
				<AppPageTitle
					title={i18n.t("bulkMessaging.title")}
					subtitle={i18n.t("bulkMessaging.subtitle")}
				/>

				<div className="card">
					<div className="card-header flex-wrap items-start justify-between gap-3 px-5 pt-5 pb-4">
						<div>
							<h6 className="card-title m-0">{i18n.t("bulkMessaging.listTitle")}</h6>
						</div>
						<button
							type="button"
							className="btn btn-sm shrink-0 bg-primary text-white"
							onClick={() => history.push("/bulk-messaging/novo")}
						>
							<IconPlus />
							{i18n.t("bulkMessaging.buttons.add")}
						</button>
					</div>

					<div className="flex min-h-0 flex-1 flex-col">
						{loading && bulkMessages.length === 0 ? (
							<div className="flex items-center justify-center py-16">
								<div className="app-modal-spinner" />
							</div>
						) : !loading && bulkMessages.length === 0 ? (
							<div className="flex flex-col items-center justify-center gap-2 px-6 py-16 text-center">
								<p className="m-0 text-sm text-default-500 dark:text-default-400">
									{i18n.t("bulkMessaging.empty")}
								</p>
							</div>
						) : (
							<>
								<div className="space-y-3 px-4 pb-4 md:hidden">
									{bulkMessages.map((bulkMessage) => (
										<BulkMobileCard
											key={bulkMessage.id}
											bulkMessage={bulkMessage}
											statusLabel={statusLabel}
											user={user}
											handleEditBulkMessages={handleEditBulkMessages}
											handleBulkMessagingStatus={handleBulkMessagingStatus}
											handleResendAllMessages={handleResendAllMessages}
											getFailedContacts={getFailedContacts}
											failedLoading={failedLoading}
											failedContacts={failedContacts}
											handleResendFailedMessages={handleResendFailedMessages}
											handleOpenBulkInfosModal={handleOpenBulkInfosModal}
											duplicateBulkMessage={duplicateBulkMessage}
											setDeletingBulkMessages={setDeletingBulkMessages}
											setConfirmModalOpen={setConfirmModalOpen}
											setPermissionOpen={setPermissionOpen}
										/>
									))}
								</div>

								<div className="hidden md:block">
									<div className="overflow-x-auto">
										<div className="inline-block min-w-full align-middle">
											<table className="min-w-full divide-y divide-default-200 dark:divide-default-700">
												<thead className="bg-default-150 dark:bg-default-800/60">
													<tr className="text-sm font-normal text-default-700 dark:text-default-300">
														<th className="px-3.5 py-3 text-center">
															{i18n.t("bulkMessaging.table.protocol")}
														</th>
														<th className="px-3.5 py-3 text-center">
															{i18n.t("bulkMessaging.table.name")}
														</th>
														<th className="px-3.5 py-3 text-center">
															{i18n.t("bulkMessaging.table.sendDate")}
														</th>
														<th className="px-3.5 py-3 text-center">
															{i18n.t("bulkMessaging.table.estimatedEnd")}
														</th>
														<th className="px-3.5 py-3 text-center">
															{i18n.t("bulkMessaging.table.status")}
														</th>
														<th className="px-3.5 py-3 text-center">
															{i18n.t("bulkMessaging.table.actions")}
														</th>
													</tr>
												</thead>
												<tbody className="divide-y divide-default-200 dark:divide-default-700">
													{bulkMessages.map((bulkMessage) => (
														<tr
															key={bulkMessage.id}
															className="text-sm text-default-800 dark:text-default-200"
														>
															<td className="whitespace-nowrap px-3.5 py-2.5 text-center">
																{bulkMessage.protocol}
															</td>
															<td className="px-3.5 py-2.5 text-center">
																<div className="inline-flex max-w-[16rem] flex-col items-center">
																	<BulkCampaignNameCell bulkMessage={bulkMessage} />
																</div>
															</td>
															<td className="whitespace-nowrap px-3.5 py-2.5 text-center">
																{formatTriggerDate(bulkMessage.trigger)}
															</td>
															<td className="whitespace-nowrap px-3.5 py-2.5 text-center">
																{formatEstimatedEnd(bulkMessage)}
															</td>
															<td className="whitespace-nowrap px-3.5 py-2.5 text-center">
																<span
																	className={`bulk-status-badge ${BULK_STATUS_CLASS[bulkMessage.status] || ""}`}
																>
																	{statusLabel(bulkMessage.status)}
																</span>
															</td>
															<td className="whitespace-nowrap px-3.5 py-2.5 text-center">
																<div className="bulk-actions-bar">
																	{canEditBulkMessage(bulkMessage) && (
																		<button
																			type="button"
																			className="bulk-action-btn"
																			title={i18n.t("bulkMessaging.actions.edit")}
																			onClick={() => handleEditBulkMessages(bulkMessage)}
																		>
																			<IconEdit />
																		</button>
																	)}

																	{bulkMessage.status === "unstarted" && (
																		<button
																			type="button"
																			className="bulk-action-btn"
																			title={i18n.t("bulkMessaging.actions.start")}
																			onClick={() =>
																				handleBulkMessagingStatus(
																					bulkMessage,
																					"running"
																				)
																			}
																		>
																			<IconPlay />
																		</button>
																	)}

																	{bulkMessage.status === "running" && (
																		<button
																			type="button"
																			className="bulk-action-btn"
																			title={i18n.t("bulkMessaging.actions.pause")}
																			onClick={() =>
																				handleBulkMessagingStatus(
																					bulkMessage,
																					"stopped"
																				)
																			}
																		>
																			<IconPause />
																		</button>
																	)}

																	{bulkMessage.status === "stopped" && (
																		<button
																			type="button"
																			className="bulk-action-btn"
																			title={i18n.t("bulkMessaging.actions.resume")}
																			onClick={() =>
																				handleBulkMessagingStatus(
																					bulkMessage,
																					"running"
																				)
																			}
																		>
																			<IconPlay />
																		</button>
																	)}

																	{(bulkMessage.status === "cancelled" ||
																		bulkMessage.status === "complete") && (
																		<>
																			<button
																				type="button"
																				className="bulk-action-btn"
																				title={i18n.t(
																					"bulkMessaging.actions.resendAllHint"
																				)}
																				onClick={() =>
																					handleResendAllMessages(bulkMessage)
																				}
																			>
																				<IconRepeat />
																			</button>

																			{bulkMessage.contacts.find(
																				(contact) => contact.jobStatus === false
																			) && (
																				<button
																					type="button"
																					className="bulk-action-btn bulk-action-btn--warn"
																					title={
																						failedLoading
																							? i18n.t(
																									"bulkMessaging.actions.loading"
																							  )
																							: i18n.t(
																									"bulkMessaging.actions.resendFailed",
																									{ count: failedContacts }
																							  )
																					}
																					onMouseEnter={() =>
																						getFailedContacts(bulkMessage.id)
																					}
																					onClick={() =>
																						handleResendFailedMessages(
																							bulkMessage
																						)
																					}
																				>
																					<IconRotateLeft />
																				</button>
																			)}
																		</>
																	)}

																	<button
																		type="button"
																		className="bulk-action-btn"
																		title={i18n.t("bulkMessaging.actions.details")}
																		onClick={() =>
																			handleOpenBulkInfosModal(bulkMessage)
																		}
																	>
																		<IconReportDetails />
																	</button>

																	<button
																		type="button"
																		className="bulk-action-btn"
																		title={i18n.t("bulkMessaging.actions.duplicate")}
																		onClick={() => duplicateBulkMessage(bulkMessage)}
																	>
																		<IconCopy />
																	</button>

																	<button
																		type="button"
																		className="bulk-action-btn"
																		title={i18n.t("bulkMessaging.actions.delete")}
																		onClick={() => {
																			if (user.profile === "admin") {
																				setDeletingBulkMessages(bulkMessage);
																				setConfirmModalOpen(true);
																			} else {
																				setPermissionOpen(true);
																			}
																		}}
																	>
																		<IconDelete />
																	</button>
																</div>
															</td>
														</tr>
													))}
												</tbody>
											</table>
										</div>
									</div>
								</div>
							</>
						)}
					</div>
				</div>
			</div>
		</div>
	);
};

export default BulkMessaging;
